import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Home,
  Search,
  SlidersHorizontal,
  Calculator,
  FileCheck2,
  FileText,
  Activity,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const HERO_SRC = "/Borrower_Journey.png";
const HERO_FALLBACK_SRC = "/Borrowers.png";
const JOURNEY_VIDEO_ID = "AkEezHaE6IA";

function JourneyWalkthroughVideo() {
  const [src, setSrc] = React.useState("");

  React.useEffect(() => {
    const origin = encodeURIComponent(window.location.origin);
    setSrc(
      `https://www.youtube.com/embed/${JOURNEY_VIDEO_ID}?rel=0&modestbranding=1&origin=${origin}`
    );
  }, []);

  return (
    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
      {src ? (
        <iframe
          className="absolute inset-0 h-full w-full rounded-xl"
          src={src}
          title="Buy to let mortgage borrower journey walkthrough"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <div className="absolute inset-0 rounded-xl bg-slate-200" aria-hidden />
      )}
    </div>
  );
}

export default function ExploreBorrowerJourney() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = HERO_SRC;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const faqs = [
    {
      question: "How does the buy to let mortgage borrower journey work?",
      answer:
        "The borrower journey starts with entering key property details such as property value, deposit amount, mortgage amount and estimated rent. Dynesourcer then shows matching buy to let mortgage products, allows borrowers to apply detailed filters, shortlist suitable options, apply online and track application progress live.",
    },
    {
      question: "What information do I need to start sourcing buy to let mortgage products?",
      answer:
        "To begin, borrowers typically enter property value, deposit amount, mortgage amount and estimated monthly rent. These inputs help generate relevant product matches and affordability-related results.",
    },
    {
      question: "Can I filter buy to let mortgage products by lender criteria?",
      answer:
        "Yes. Borrowers can refine search results using filters such as LTV, EPC rating, mortgage term, product term, borrower type, loan purpose, tenancy type, taxpayer type, leasehold status and more.",
    },
    {
      question: "Can I apply for a buy to let mortgage online through Dynesourcer?",
      answer:
        "Yes. Borrowers can shortlist up to 5 products, create an account, register an online application and receive an instant mortgage illustration for the selected scenario.",
    },
    {
      question: "Can I track my mortgage application after submission?",
      answer:
        "Yes. Dynesourcer allows borrowers to monitor the progress of their application through the portal after it has been submitted.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  React.useEffect(() => {
    document.title = "Buy to Let Mortgage Process UK | Borrower Journey | Dynesourcer";

    const description =
      "Explore the full buy to let mortgage borrower journey in the UK, from entering property details and sourcing products to shortlisting, applying online and tracking progress live.";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = description;
      document.head.appendChild(meta);
    }

    const schemaId = "explore-borrower-journey-faq-schema";
    let schemaScript = document.getElementById(schemaId);
    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.id = schemaId;
      schemaScript.type = "application/ld+json";
      document.head.appendChild(schemaScript);
    }
    schemaScript.text = JSON.stringify(faqSchema);
  }, []);

  return (
    <>
      <main className="min-h-screen bg-white text-slate-800">
        <TopNav />

        <section className="relative h-[520px] w-full overflow-hidden hero-landscape-responsive">
          <img
            src={HERO_SRC}
            alt="Buy to let mortgage borrower journey UK from sourcing to application tracking"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            fetchPriority="high"
            onError={(e) => {
              e.currentTarget.src = HERO_FALLBACK_SRC;
            }}
          />
          <div className="absolute inset-0 bg-sky-950/55" />
          <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white">
            <h1 className="text-4xl font-black tracking-tight md:text-6xl">
              Buy to Let Mortgage Borrower Journey UK
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/90 md:text-lg">
              Discover how Dynesourcer helps borrowers move from initial buy to
              let mortgage search to product comparison, online application and
              live tracking. Explore the full UK borrower journey step by step.
            </p>
            <Link
              to="/sourcing/buy-to-let-mortgage-sourcing"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-sky-500 px-6 py-3 text-lg font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-lg"
            >
              Source BTL products now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        <section className="bg-sky-50 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Understand the Buy to Let Mortgage Process
              </h2>
              <p className="mt-3 text-slate-600">
                This page explains the full borrower journey for UK buy to let
                mortgages, from entering property details and viewing product
                matches to narrowing results with lender criteria, applying
                online and tracking application progress in real time.
              </p>
            </div>

            <div className="mx-auto mt-8 max-w-5xl rounded-2xl border border-sky-100 bg-white p-4 shadow-sm md:p-6">
              <div className="overflow-hidden rounded-xl bg-slate-100">
                <JourneyWalkthroughVideo />
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Watch the borrower journey walkthrough to see how the platform
                supports faster product discovery, shortlisting, online
                application and live case tracking.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Complete Buy to Let Borrower Flow
              </h2>
              <p className="mt-3 text-slate-600">
                Dynesourcer is built to simplify the UK buy to let mortgage
                process by helping borrowers move from search inputs to
                shortlisted mortgage products and submitted applications in one
                connected workflow.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: <Home className="h-5 w-5" />,
                  title: "1. Enter property and borrowing details",
                  text:
                    "Borrowers start by entering property value, deposit amount, mortgage amount and estimated rent to begin the buy to let mortgage search.",
                },
                {
                  icon: <Search className="h-5 w-5" />,
                  title: "2. View products instantly",
                  text:
                    "Suitable products appear in milliseconds with key information such as interest rate, monthly repayment, arrangement fee and cost over the product term.",
                },
                {
                  icon: <SlidersHorizontal className="h-5 w-5" />,
                  title: "3. Refine results with lender filters",
                  text:
                    "Borrowers can narrow their search using detailed criteria including LTV, EPC rating, mortgage term, product term, borrower type, loan purpose and more.",
                },
                {
                  icon: <Calculator className="h-5 w-5" />,
                  title: "4. Use auto-calculation features",
                  text:
                    "If a deposit amount is entered, mortgage amount is calculated automatically. If LTV is changed, both mortgage and deposit values update accordingly.",
                },
                {
                  icon: <FileCheck2 className="h-5 w-5" />,
                  title: "5. Shortlist products and apply online",
                  text:
                    "Borrowers can shortlist up to 5 products, sign up or sign in, complete an online application and receive an instant mortgage illustration.",
                },
                {
                  icon: <Activity className="h-5 w-5" />,
                  title: "6. Track mortgage progress live",
                  text:
                    "After submission, borrowers can monitor the progress of their mortgage application directly within the portal.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-sky-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-lg"
                >
                  <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-50 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Why this borrower journey is useful
              </h2>
              <p className="mt-3 text-slate-600">
                Many mortgage websites stop at calculators or simple lead forms.
                Dynesourcer goes further by helping borrowers search, compare,
                shortlist, apply and track their buy to let mortgage journey in
                one place.
              </p>
            </div>

            <div className="mx-auto mt-8 max-w-4xl space-y-4 text-slate-600">
              <p>
                For UK borrowers, the buy to let mortgage process can feel
                fragmented when product sourcing, lender criteria, illustrations
                and application updates are spread across separate systems. This
                borrower journey page explains how the process becomes more
                efficient when everything is connected through a single platform.
              </p>
              <p>
                From first-time landlords to experienced investors, users can
                start with the core financial inputs, review matching products
                quickly, refine their search using detailed lender filters and
                then progress to online application without repeating steps.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-center text-3xl font-black text-slate-900 md:text-4xl">
              Filters Available During Sourcing
            </h2>
            <p className="mt-3 text-center text-slate-600">
              Borrowers can apply major lender criteria, including taxpayer-rate
              based filtering for more accurate buy to let product results.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                "LTV",
                "EPC rating",
                "Mortgage term",
                "Product term",
                "Product type",
                "Loan purpose",
                "Borrower type",
                "Home owner",
                "BTL experience",
                "Credit worthiness",
                "Tenancy Type",
                "Taxpayer type (DSC)",
                "F/H or L/H (FREEHOLD / LEASEHOLD)",
                "Leasehold remaining term",
                "Ex Local Authority",
                "Deposit Origination",
                "Day 1 Remortgage",
              ].map((filter) => (
                <div
                  key={filter}
                  className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-medium text-slate-700 transition-all hover:border-sky-200 hover:bg-sky-100"
                >
                  {filter}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-50 py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4">
            <div className="text-center">
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Frequently Asked Questions
              </h2>
              <p className="mt-3 text-slate-600">
                Common questions about the buy to let mortgage borrower journey.
              </p>
            </div>

            <div className="mt-10 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.question}
                  className="rounded-2xl border border-sky-100 bg-white p-6 shadow-sm"
                >
                  <h3 className="text-lg font-bold text-slate-900">{faq.question}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-500 py-14 text-center text-white">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-black md:text-4xl">
              Ready to begin your buy to let mortgage journey?
            </h2>
            <p className="mt-3 text-white/90">
              Start sourcing BTL products, compare suitable options, shortlist
              products and continue into online application.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/sourcing/buy-to-let-mortgage-sourcing"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-semibold text-sky-700 transition-all hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-lg"
              >
                Source BTL products now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/buy-to-let-mortgage-calculator"
                className="inline-flex items-center gap-2 rounded-lg border border-white/40 bg-white/10 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/20"
              >
                <FileText className="h-4 w-4" /> View BTL Mortgage Calculator
              </Link>
            </div>
          </div>
        </section>

        <SiteFooter />
      </main>
    </>
  );
}